/**
 * The "E-Mail-Vorlage" tab: one automation's template — read, edit, preview,
 * review, save, go back to the system template, and its history (D10).
 *
 * WHAT IS IN FORCE, SAID PLAINLY. "Systemvorlage" when no own template governs
 * (nothing stored, or the newest stored step was "back to the system
 * template"); "Eigene Vorlage · Version n" when one does. The system template
 * has no version and gets none here — it is code on the server, not a stored
 * document, so it is shown only as the server renders it.
 *
 * EDITING ONLY WHERE THE SERVER ALLOWS IT. `capabilities.custom_authoring` of
 * the template decides: the birthday greeting may be authored, the requirement
 * reminder (today) may not. A template that cannot be authored is shown with
 * its catalogue and its preview, and with no editor and no save — the page
 * never decides that from the category or the name.
 *
 * A NEW OWN TEMPLATE STARTS EMPTY. The system template cannot be expressed as
 * a `doc/v1` document (the server keeps it as code and returns no document for
 * it), so there is no faithful copy to start from; an invented one would be a
 * second system template that nobody reviewed. The preview beside it keeps
 * showing the system letter for comparison until the draft is previewed.
 * Empty, but complete (D12a): where the server names structural blocks for the
 * body (`structural_blocks` — the reminder's list of outstanding obligations),
 * the draft starts with each of them between two empty paragraphs.
 *
 * ONE LETTER, SEVERAL EXAMPLES (D12a). Where the template has preview
 * scenarios, an own template is previewed in the chosen one as well: the
 * scenario's example obligations fill the structural block, so the reader can
 * see the one letter in every variant. The scenario is sent with the custom
 * preview and is part of what makes a preview current.
 *
 * REVIEW BEFORE SAVE, AND ONLY ON A CURRENT PREVIEW. There is no "save" in the
 * editor. "Änderungen prüfen" renders the draft on the server (unless the
 * shown preview already is the draft's), and only a successful, current
 * rendering opens the review; any edit after that makes it stale again. The
 * review saves exactly the reviewed documents, with `expected_current_version`
 * from the server, and applies exactly the version that comes back.
 *
 * GOING BACK IS A CONFIRMED NEW VERSION. "Auf Systemvorlage zurücksetzen"
 * appends a `system` step after a confirmation; the own versions stay in the
 * history, nothing is deleted.
 *
 * A DRAFT LIVES AS LONG AS THIS AUTOMATION IS SHOWN. Switching tabs keeps it
 * (the panel stays mounted); choosing another automation discards it, because
 * this component is keyed by the automation. The repo has no unsaved-changes
 * guard for navigation, and none is improvised here; the status bar says
 * "Ungespeicherte Änderungen" for as long as there are some.
 *
 * THE TEST SEND SITS IN THE STATUS BAR (TEST-EMAIL T3), beside the template it
 * sends, and only where the automation's `capabilities.test_email` offers one.
 * The server sends the STORED template, so while this tab holds unsaved work
 * the button stays but does not open, and says why. Its dialog, its request
 * and its send lock live in `AutomationTestEmailAction`, not here.
 */
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import AutomationTemplateEditor from "./AutomationTemplateEditor";
import AutomationTemplateHistory from "./AutomationTemplateHistory";
import AutomationTemplatePlaceholders from "./AutomationTemplatePlaceholders";
import AutomationTemplatePreview from "./AutomationTemplatePreview";
import AutomationTemplateReview from "./AutomationTemplateReview";
import AutomationTemplateStatus from "./AutomationTemplateStatus";
import AutomationTestEmailAction from "./AutomationTestEmailAction";
import {
  absoluteOffset,
  addBlock,
  addListItem,
  draftFromDocs,
  draftProblems,
  draftToDocs,
  emptyDraft,
  insertVariable,
  locateRange,
  mapParagraph,
  marksOf,
  paragraphsOf,
  removeBlock,
  removeListItem,
  removeVariable,
  sanitizeText,
  serializeDraft,
  setBlockType,
  subjectTextLength,
  toggleMark,
  updateSegmentText,
} from "./automationTemplateDoc";
import { TEST_EMAIL_SAVING_NOTE, TEST_EMAIL_UNSAVED_NOTE } from "./automationTestEmailModel";
import useAutomationTemplate from "./useAutomationTemplate";

const SECONDARY_BUTTON =
  "inline-flex items-center justify-center rounded-[9px] border border-border bg-card px-3.5 py-1.5 text-[12.5px] font-semibold text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 max-md:min-h-9";

const systemBasis = (scenarioKey) => `system|${scenarioKey ?? ""}`;
const customBasis = (serialized, scenarioKey = null) =>
  scenarioKey ? `custom|${scenarioKey}|${serialized}` : `custom|${serialized}`;
/** A custom preview request; it names the scenario only where there is one. */
const customRequest = (docs, scenarioKey = null) => ({
  source: "custom",
  ...docs,
  ...(scenarioKey ? { scenario_key: scenarioKey } : {}),
});
const docOptionsOf = (data) => ({
  structuralBlocks: Array.isArray(data?.structural_blocks) ? data.structural_blocks : [],
});

export default function AutomationTemplateTab({ automation, active, onTestEmailAttempted }) {
  const template = useAutomationTemplate(automation.key);
  const { state, preview, versions, write, loadState, requestPreview, rebasePreview } =
    template;
  const data = state.data;
  const canAuthor = data?.capabilities?.custom_authoring === true;
  const scenarios = useMemo(
    () => (Array.isArray(data?.preview_scenarios) ? data.preview_scenarios : []),
    [data]
  );
  const placeholders = useMemo(
    () => (Array.isArray(data?.placeholders) ? data.placeholders : []),
    [data]
  );
  const limits = data?.limits || {};
  const docOptions = useMemo(() => docOptionsOf(data), [data]);

  const [scenarioKey, setScenarioKey] = useState(null);
  const [draft, setDraft] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [creating, setCreating] = useState(false);
  const [target, setTarget] = useState(null);
  const [problems, setProblems] = useState([]);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewRequested, setReviewRequested] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [pendingFocus, setPendingFocus] = useState(null);
  const reviewHeading = useRef(null);
  const testEmailNoteId = useId();

  // ── focus: back to what opened a confirmation (Final QA) ─────────────
  // The two confirmations are opened by state, not by a Radix trigger, and
  // Radix returns focus to its trigger — none here — so closing one dropped
  // focus onto the page body. The element that had focus when it opened gets
  // it back, if it is still there.
  const confirmOpener = useRef(null);
  const openConfirm = (which) => {
    confirmOpener.current = document.activeElement;
    setConfirm(which);
  };
  const returnFocusFromConfirm = (event) => {
    event.preventDefault();
    const opener = confirmOpener.current;
    confirmOpener.current = null;
    if (opener && opener.isConnected) opener.focus();
  };

  // ── focus: where the editor moves the caret ──────────────────────────
  const fields = useRef(new Map());
  const registerField = useCallback((id, el) => {
    if (el) fields.current.set(id, el);
    else fields.current.delete(id);
  }, []);
  useEffect(() => {
    if (!pendingFocus) return;
    const el = fields.current.get(pendingFocus.segmentId);
    if (el) {
      el.focus();
      try {
        el.setSelectionRange(pendingFocus.start, pendingFocus.end);
      } catch (_) {
        // A field that cannot take a selection still has the focus.
      }
    }
    setTarget({
      field: pendingFocus.field,
      paragraphId: pendingFocus.paragraphId,
      segmentId: pendingFocus.segmentId,
      start: pendingFocus.start,
      end: pendingFocus.end,
    });
    setPendingFocus(null);
  }, [pendingFocus]);

  useEffect(() => {
    if (reviewOpen && reviewHeading.current) reviewHeading.current.focus();
  }, [reviewOpen]);

  // ── the server's template, applied ───────────────────────────────────
  const applyServerState = useCallback(
    (next) => {
      const firstScenario = Array.isArray(next.preview_scenarios)
        ? next.preview_scenarios[0]?.key ?? null
        : null;
      setScenarioKey(firstScenario);
      setCreating(false);
      setReviewOpen(false);
      setReviewRequested(false);
      setProblems([]);
      setTarget(null);
      if (next.effective_source === "custom") {
        const stored = draftFromDocs(next.subject_doc, next.body_doc, docOptionsOf(next));
        const serialized = serializeDraft(stored);
        if (next.capabilities?.custom_authoring === true) {
          setDraft(stored);
          setBaseline(serialized);
        } else {
          setDraft(null);
          setBaseline(null);
        }
        requestPreview(
          customRequest({ subject_doc: next.subject_doc, body_doc: next.body_doc }, firstScenario),
          customBasis(serialized, firstScenario)
        );
      } else {
        setDraft(null);
        setBaseline(null);
        requestPreview(
          { source: "system", scenario_key: firstScenario ?? undefined },
          systemBasis(firstScenario)
        );
      }
    },
    [requestPreview]
  );

  const loadAndApply = useCallback(async () => {
    const next = await loadState();
    if (next) applyServerState(next);
  }, [loadState, applyServerState]);

  useEffect(() => {
    if (active && state.status === "idle") loadAndApply();
  }, [active, state.status, loadAndApply]);

  // ── derived: dirty, and which rendering is current ───────────────────
  const editing = draft !== null;
  const serializedDraft = useMemo(() => (draft ? serializeDraft(draft) : null), [draft]);
  const dirty = editing && serializedDraft !== baseline;
  const savedSerialized = useMemo(
    () =>
      data?.effective_source === "custom"
        ? serializeDraft(draftFromDocs(data.subject_doc, data.body_doc, docOptions))
        : null,
    [data, docOptions]
  );
  const savedCustomBasis =
    savedSerialized === null ? null : customBasis(savedSerialized, scenarioKey);
  const currentBasis = editing
    ? customBasis(serializedDraft, scenarioKey)
    : data?.effective_source === "custom"
      ? savedCustomBasis
      : systemBasis(scenarioKey);
  const previewMatches = preview.basis !== null && preview.basis === currentBasis;
  const previewCurrent = previewMatches && preview.status === "success";
  const previewBasisLabel = !preview.basis
    ? "Noch keine Vorschau"
    : preview.basis.startsWith("system|")
      ? "Systemvorlage"
      : preview.basis === savedCustomBasis
        ? "Gespeicherte eigene Vorlage"
        : "Entwurf";

  // An edit takes back whatever the last check said about the draft.
  useEffect(() => {
    setProblems([]);
  }, [serializedDraft]);

  useEffect(() => {
    if (!reviewRequested) return;
    if (preview.basis !== currentBasis) {
      if (preview.status !== "loading") setReviewRequested(false);
      return;
    }
    if (preview.status === "success") {
      setReviewOpen(true);
      setReviewRequested(false);
    } else if (preview.status === "error") {
      setReviewRequested(false);
    }
  }, [reviewRequested, preview.status, preview.basis, currentBasis]);

  // ── preview ──────────────────────────────────────────────────────────
  const refreshPreview = (key = scenarioKey) => {
    if (editing) {
      const found = draftProblems(draft, limits, docOptions);
      setProblems(found);
      if (found.length) return;
      requestPreview(customRequest(draftToDocs(draft), key), customBasis(serializedDraft, key));
    } else if (data?.effective_source === "custom") {
      requestPreview(
        customRequest({ subject_doc: data.subject_doc, body_doc: data.body_doc }, key),
        customBasis(savedSerialized, key)
      );
    } else {
      requestPreview({ source: "system", scenario_key: key ?? undefined }, systemBasis(key));
    }
  };

  /** Another example: the same letter — system or own — in that scenario. */
  const changeScenario = (key) => {
    setScenarioKey(key);
    refreshPreview(key);
  };

  // ── editing ──────────────────────────────────────────────────────────
  const startCreating = () => {
    const fresh = emptyDraft(docOptions);
    setDraft(fresh);
    setBaseline(serializeDraft(fresh));
    setCreating(true);
    setProblems([]);
    setPendingFocus({
      field: "subject",
      paragraphId: fresh.subject.id,
      segmentId: fresh.subject.segments[0].id,
      start: 0,
      end: 0,
    });
  };

  const paragraphFor = (field, paragraphId) =>
    field === "subject"
      ? draft.subject
      : paragraphsOf(draft.body).find((paragraph) => paragraph.id === paragraphId) || null;

  const replaceParagraph = (field, paragraphId, next) => {
    setDraft((current) =>
      field === "subject"
        ? { ...current, subject: next }
        : { ...current, body: mapParagraph(current.body, paragraphId, () => next) }
    );
  };

  const fieldPropsFor = (field) => (paragraph) => ({
    registerField,
    singleLine: field === "subject",
    onTextChange: (segmentId, value) => {
      const text = sanitizeText(value, { singleLine: field === "subject" });
      setDraft((current) => {
        const fn = (p) => updateSegmentText(p, segmentId, text);
        return field === "subject"
          ? { ...current, subject: fn(current.subject) }
          : { ...current, body: mapParagraph(current.body, paragraph.id, fn) };
      });
    },
    onTarget: (segmentId, start, end) =>
      setTarget({ field, paragraphId: paragraph.id, segmentId, start, end }),
    onRemoveVariable: (segmentId) => {
      const current = paragraphFor(field, paragraph.id);
      if (!current) return;
      const result = removeVariable(current, segmentId);
      replaceParagraph(field, paragraph.id, result.paragraph);
      if (result.caret) {
        setPendingFocus({
          field,
          paragraphId: paragraph.id,
          segmentId: result.caret.segmentId,
          start: result.caret.offset,
          end: result.caret.offset,
        });
      } else {
        setTarget(null);
      }
    },
    onNavigate: (segmentId, offset) =>
      setPendingFocus({ field, paragraphId: paragraph.id, segmentId, start: offset, end: offset }),
  });

  const insertPlaceholder = (key) => {
    if (!editing || !target) return;
    const current = paragraphFor(target.field, target.paragraphId);
    if (!current) return;
    const result = insertVariable(current, target.segmentId, target.start, key, {
      selectionEnd: target.end,
      inheritMarks: target.field !== "subject",
    });
    if (!result.caretSegmentId) return;
    replaceParagraph(target.field, target.paragraphId, result.paragraph);
    setPendingFocus({
      field: target.field,
      paragraphId: target.paragraphId,
      segmentId: result.caretSegmentId,
      start: 0,
      end: 0,
    });
  };

  const bodyTarget =
    editing && target?.field === "body" ? paragraphFor("body", target.paragraphId) : null;
  const activeMarks = bodyTarget ? marksOf(bodyTarget, target.segmentId) : [];

  const toggleMarkOnTarget = (mark) => {
    if (!bodyTarget) return;
    const from = absoluteOffset(bodyTarget, target.segmentId, target.start);
    const to = absoluteOffset(bodyTarget, target.segmentId, target.end);
    const next = toggleMark(bodyTarget, target.segmentId, target.start, target.end, mark);
    replaceParagraph("body", bodyTarget.id, next);
    const located = locateRange(next, from, to);
    if (located) {
      setPendingFocus({ field: "body", paragraphId: bodyTarget.id, ...located });
    }
  };

  const updateBody = (fn) => {
    setDraft((current) => ({ ...current, body: fn(current.body) }));
    setTarget((current) => (current?.field === "body" ? null : current));
  };

  // ── review and the two writes ────────────────────────────────────────
  const startReview = () => {
    if (!editing) return;
    const found = draftProblems(draft, limits, docOptions);
    setProblems(found);
    if (found.length) return;
    template.clearWriteError();
    if (previewCurrent) {
      setReviewOpen(true);
      return;
    }
    setReviewRequested(true);
    requestPreview(
      customRequest(draftToDocs(draft), scenarioKey),
      customBasis(serializedDraft, scenarioKey)
    );
  };

  const saveVersion = async () => {
    if (!editing || !previewCurrent) return;
    const docs = draftToDocs(draft);
    const version = await template.createVersion("save", {
      source: "custom",
      ...docs,
      expected_current_version: data.latest_version ?? null,
    });
    if (!version) return;
    const stored = draftFromDocs(version.subject_doc, version.body_doc, docOptions);
    const serialized = serializeDraft(stored);
    setDraft(stored);
    setBaseline(serialized);
    setCreating(false);
    setReviewOpen(false);
    setTarget(null);
    if (serialized === serializedDraft) {
      // The server stored exactly what it rendered for the review.
      rebasePreview(customBasis(serialized, scenarioKey));
    } else {
      // It normalised something: render what it stored, not what was sent.
      requestPreview(
        customRequest({ subject_doc: version.subject_doc, body_doc: version.body_doc }, scenarioKey),
        customBasis(serialized, scenarioKey)
      );
    }
    template.refreshVersionsIfLoaded();
    toast.success(`Vorlage gespeichert – Version ${version.version}.`);
  };

  const revertToSystem = async () => {
    setConfirm(null);
    const version = await template.createVersion("revert", {
      source: "system",
      expected_current_version: data.latest_version ?? null,
    });
    if (!version) return;
    setDraft(null);
    setBaseline(null);
    setCreating(false);
    setReviewOpen(false);
    setTarget(null);
    setProblems([]);
    requestPreview(
      { source: "system", scenario_key: scenarioKey ?? undefined },
      systemBasis(scenarioKey)
    );
    template.refreshVersionsIfLoaded();
    toast.success("Die Systemvorlage ist wieder aktiv.");
  };

  const discardChanges = () => {
    setConfirm(null);
    setReviewOpen(false);
    setReviewRequested(false);
    setProblems([]);
    setTarget(null);
    template.clearWriteError();
    let nextBasis;
    if (creating || data.effective_source !== "custom") {
      setDraft(null);
      setBaseline(null);
      setCreating(false);
      nextBasis = systemBasis(scenarioKey);
      if (!(preview.basis === nextBasis && preview.status === "success")) {
        requestPreview({ source: "system", scenario_key: scenarioKey ?? undefined }, nextBasis);
      }
    } else {
      const stored = draftFromDocs(data.subject_doc, data.body_doc, docOptions);
      setDraft(stored);
      setBaseline(serializeDraft(stored));
      nextBasis = savedCustomBasis;
      if (!(preview.basis === nextBasis && preview.status === "success")) {
        requestPreview(
          customRequest({ subject_doc: data.subject_doc, body_doc: data.body_doc }, scenarioKey),
          nextBasis
        );
      }
    }
  };

  const reloadServerState = async () => {
    template.clearWriteError();
    const next = await loadState();
    if (next) applyServerState(next);
  };

  // ── render ───────────────────────────────────────────────────────────
  if (state.status === "idle" || (state.status === "loading" && !data)) {
    return (
      <p className="m-0 text-[12.5px] text-muted-foreground">E-Mail-Vorlage wird geladen …</p>
    );
  }
  if (state.status === "error" && !data) {
    return (
      <div className="flex flex-col items-start gap-2">
        <p role="alert" className="m-0 text-[12.5px] text-destructive">
          {state.error?.message}
        </p>
        <button type="button" onClick={loadAndApply} className={SECONDARY_BUTTON}>
          Erneut versuchen
        </button>
      </div>
    );
  }

  const isCustom = data.effective_source === "custom";
  const saving = write.status === "pending";
  const saveError = write.kind === "save" ? write.error : null;
  const revertError = write.kind === "revert" ? write.error : null;
  const subjectLength = editing ? subjectTextLength(draft.subject) : 0;
  // TEST-EMAIL T3: where the server offers a test send, beside the template it
  // sends — the STORED one, so not while anything here is unsaved or saving.
  const canTestEmail = automation.capabilities?.test_email === true;
  // Only while there is an editor: a review state left over without one (a
  // revert answering before a requested review's preview) is not unsaved work
  // and must not keep the test send shut (adversarial review).
  const unsavedTemplate = editing && (creating || dirty || reviewOpen || reviewRequested);
  const testEmailBlocked = unsavedTemplate || saving;

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <AutomationTemplateStatus
        source={data.effective_source}
        latestVersion={data.latest_version}
        creating={creating}
        dirty={dirty}
        canAuthor={canAuthor}
        canCreate={canAuthor && !isCustom && !editing}
        onCreate={startCreating}
        canRevert={isCustom}
        revertDisabled={saving || reviewOpen}
        onRevert={() => openConfirm("revert")}
        revertError={revertError}
        onReload={reloadServerState}
        testEmailAction={
          canTestEmail ? (
            <AutomationTestEmailAction
              automation={automation}
              blocked={testEmailBlocked}
              describedBy={testEmailNoteId}
              onAttempted={onTestEmailAttempted}
            />
          ) : null
        }
        testEmailNote={
          canTestEmail && testEmailBlocked
            ? {
                id: testEmailNoteId,
                // A save started from the review is still "unsaved" until it
                // answers; while it runs, that it runs is what is true.
                text: saving ? TEST_EMAIL_SAVING_NOTE : TEST_EMAIL_UNSAVED_NOTE,
              }
            : null
        }
      />

      <div className="grid grid-cols-1 gap-4 min-[1640px]:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] min-[1640px]:items-start">
        <div className="flex min-w-0 flex-col gap-4">
          {reviewOpen && editing ? (
            <AutomationTemplateReview
              ref={reviewHeading}
              replaces={isCustom ? `Version ${data.latest_version}` : "Die Systemvorlage"}
              result={preview.result}
              saving={saving}
              canSave={previewCurrent}
              saveError={saveError}
              onBack={() => setReviewOpen(false)}
              onSave={saveVersion}
              onReload={reloadServerState}
            />
          ) : editing ? (
            <AutomationTemplateEditor
              draft={draft}
              limits={limits}
              subjectLength={subjectLength}
              fieldPropsFor={fieldPropsFor}
              placeholders={placeholders}
              canInsert={!!target}
              onInsertPlaceholder={insertPlaceholder}
              bodyEditorProps={{
                activeMarks,
                marksEnabled: !!bodyTarget,
                onToggleMark: toggleMarkOnTarget,
                onAddBlock: (type) => updateBody((body) => addBlock(body, type)),
                onRemoveBlock: (blockId) => updateBody((body) => removeBlock(body, blockId)),
                onSetBlockType: (blockId, type) =>
                  updateBody((body) => setBlockType(body, blockId, type)),
                onAddListItem: (blockId) => updateBody((body) => addListItem(body, blockId)),
                onRemoveListItem: (blockId, itemId) =>
                  updateBody((body) => removeListItem(body, blockId, itemId)),
              }}
              problems={problems}
              canDiscard={dirty || creating}
              onDiscard={() => openConfirm("discard")}
              reviewDisabled={!dirty || reviewRequested}
              reviewPending={reviewRequested}
              onReview={startReview}
            />
          ) : (
            <div className="flex min-w-0 flex-col gap-3 rounded-[14px] border border-border bg-card p-3.5">
              <p className="m-0 text-[12.5px] text-foreground">
                {isCustom
                  ? "Die eigene Vorlage ist gespeichert; die Vorschau zeigt sie mit Beispieldaten."
                  : "Die Systemvorlage ist im System hinterlegt; die Vorschau zeigt sie mit Beispieldaten."}
              </p>
              <div className="flex flex-col gap-1.5">
                <span className="text-[12.5px] font-semibold text-foreground">
                  Verfügbare Platzhalter
                </span>
                <AutomationTemplatePlaceholders placeholders={placeholders} readOnly />
              </div>
            </div>
          )}
        </div>

        <AutomationTemplatePreview
          preview={preview}
          isCurrent={previewMatches}
          basisLabel={previewBasisLabel}
          onRefresh={() => refreshPreview()}
          refreshDisabled={reviewOpen}
          scenarios={scenarios}
          scenarioKey={scenarioKey}
          onScenarioChange={changeScenario}
          scenarioEnabled={canAuthor || (!editing && !isCustom)}
          placeholders={placeholders}
        />
      </div>

      <AutomationTemplateHistory
        versions={versions}
        latestVersion={data.latest_version ?? null}
        onLoad={template.loadVersions}
      />

      <AlertDialog
        open={confirm === "revert"}
        onOpenChange={(open) => {
          if (!open) setConfirm(null);
        }}
      >
        <AlertDialogContent onCloseAutoFocus={returnFocusFromConfirm}>
          <AlertDialogHeader>
            <AlertDialogTitle>Auf Systemvorlage zurücksetzen?</AlertDialogTitle>
            <AlertDialogDescription>
              Für neue E-Mails gilt danach wieder die Systemvorlage. Die eigene Vorlage bleibt
              im Versionsverlauf erhalten.
              {dirty ? " Ungespeicherte Änderungen gehen verloren." : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={revertToSystem}>Zurücksetzen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={confirm === "discard"}
        onOpenChange={(open) => {
          if (!open) setConfirm(null);
        }}
      >
        <AlertDialogContent onCloseAutoFocus={returnFocusFromConfirm}>
          <AlertDialogHeader>
            <AlertDialogTitle>Änderungen verwerfen?</AlertDialogTitle>
            <AlertDialogDescription>
              {creating
                ? "Der Entwurf der eigenen Vorlage wird verworfen; es bleibt bei der Systemvorlage."
                : "Die Bearbeitung kehrt zur gespeicherten Vorlage zurück."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={discardChanges}>Verwerfen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
